import {Placement} from '@popperjs/core';
import React, {FC, PropsWithChildren} from 'react';
import styled from 'styled-components';

import {globalWindow} from '../../../../helpers/src/browser/browserHelpers';
import {shadows} from '../../styles/colorStyles';
import {boxShadow} from '../../theme/themeStyleHelpers';
import {Anchor, buildReferenceObject} from '../reposition/repositionHelpers';
import {RepositionPopover, RepositionPopoverProps} from './repositionPopover';

/*
 * Constants.
 */

const DEFAULT_HEIGHT = 316;
const DEFAULT_WIDTH = 270;
const DEFAULT_MARGIN = 20;

/*
 * Props.
 */

export interface PreviewPopperProps extends RepositionPopoverProps {
  height?: number;
  width?: number;
  margin?: number;
  className?: string;
}

interface PreviewWrapperStyleProps {
  $height: number;
  $width: number;
  $margin: number;
  $placement: Placement;
}

/*
 * Style.
 */

const StyledPreviewWrapper = styled.div<PreviewWrapperStyleProps>`
  background-color: ${(p) => p.theme.foreground};
  width: ${(p) => p.$width}px;
  height: ${(p) => p.$height}px;
  margin: ${(p) => p.$margin}px;
  border-radius: 8px;
  overflow-y: auto;
  ${shadows.dialogsAndDropdowns};

  ::before {
    content: '';
    border-width: 10px;
    border-style: solid;
    border-color: ${(p) => p.theme.alphas.transparent} ${(p) => p.theme.alphas.transparent}
      ${(p) => p.theme.greys.white} ${(p) => p.theme.greys.white};
    border-bottom-left-radius: 4px;
    ${(p) => boxShadow(p.theme, `-0.5px 0.5px 0 ${p.theme.alphas.black40}`)};
    position: absolute;
    top: 50%;
    ${(p) => (p.$placement === 'left' ? 'right' : 'left')}: 0;
    ${(p) => (p.$placement === 'left' ? 'margin-right : 17px' : 'margin-left : 3px')};
    transform: ${(p) =>
      p.$placement === 'right' ? 'rotate(45deg) translateY(-50%)' : 'rotate(225deg) translateY(50%)'};
  }
`;

/*
 * Component.
 */

export const PreviewPopper: FC<PropsWithChildren<PreviewPopperProps>> = ({
  children,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  margin = DEFAULT_MARGIN,
  className,
  ...repositionPopoverProps
}) => {
  const placement = getPlacement(repositionPopoverProps.anchor, width, margin);
  if (!placement) {
    return null;
  }

  return (
    <RepositionPopover {...repositionPopoverProps} placement={placement}>
      <StyledPreviewWrapper
        $height={height}
        $width={width}
        $margin={margin}
        $placement={placement}
        className={className}
      >
        {children}
      </StyledPreviewWrapper>
    </RepositionPopover>
  );
};

/*
 * Helpers.
 */

function getPlacement(anchor: Anchor, width: number, margin: number) {
  const element = buildReferenceObject(anchor);
  if (!element) {
    return null;
  }

  // Is there enough horizontal space to fit the popper?
  const {right} = element.getBoundingClientRect();
  const {innerWidth: screenWidth} = globalWindow;
  const fullSize = margin * 2 + width + 20; // Popper seems to add 20px to the needed size for positioning.

  if (screenWidth - right >= fullSize) {
    return 'right';
  }
  return 'left';
}
